import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "ABOUT US", href: "#about" },
    { name: "OUR SERVICES", href: "#services" },
    { name: "WORK", href: "#work" },
    { name: "CONTACT", href: "#contact" },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          data-testid="logo"
          className="flex items-center"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_automate-scale-4/artifacts/6ae3ntbm_TAKEOFF%20LOGOS%20%28350%20x%20180%20px%29%20%283%29.png" 
            alt="TAKEOFF Marketing Group"
            className="h-16 md:h-20 w-auto"
          />
        </a>

        {/* Desktop Navigation */}
        <nav
          data-testid="desktop-nav"
          className="hidden md:flex items-center gap-8"
        >
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => scrollToSection(link.href)}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-body text-sm text-white/80 hover:text-lime transition-colors duration-200 tracking-wide"
            >
              {link.name}
            </button>
          ))}
        </nav>

        {/* CTA Button */}
        <button
          onClick={() => scrollToSection("#contact")}
          data-testid="get-started-btn"
          className="hidden md:flex items-center gap-2 bg-lime text-black font-body font-semibold text-sm px-5 py-2.5 hover:bg-lime-hover transition-colors duration-200"
        >
          GET STARTED
          <ArrowRight className="w-4 h-4" />
        </button>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-white p-2"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          data-testid="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 bg-black/90 backdrop-blur-xl border-b border-white/10"
        >
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="font-body text-left text-white/80 hover:text-lime transition-colors py-2"
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={() => scrollToSection("#contact")}
              className="flex items-center gap-2 bg-lime text-black font-body font-semibold text-sm px-5 py-3 mt-2 justify-center"
            >
              GET STARTED
              <ArrowRight className="w-4 h-4" />
            </button>
          </nav>
        </motion.div>
      )}
    </motion.header>
  );
}
